import type { StaffRole } from './staff-roles'

/**
 * DRAWERS — single source of truth (Kelvo item 2).
 *
 * A Kelvo client's documents live in drawers, exactly as agreed at sign-off and
 * printed on The 7 Drawers card. A drawer is the ACCESS UNIT: every document,
 * and every fact the assistant answers from, sits in one drawer, and a staff
 * role reaches a fixed set of drawers. Nothing else decides who sees what.
 *
 * WHY ONE DRAWER EACH, NOT FREE TAGS. With several tags per item, "may the site
 * team see this?" becomes a union over tags, and one careless tag on a supplier
 * statement opens it to everyone on site. One drawer each keeps the question
 * physical — which drawer is it in? — and matches the filing cabinet the owner
 * was sold.
 *
 * WHY DRAWER 06 IS TWO KEYS. Sign-off gives the site team PART of 06 Process:
 * the FAQ and new-starter notes, not the phone-call-to-invoice write-up. A
 * per-document "site may see this" flag would be a second access rule beside
 * the drawer, and two rules drift. So the shared part is its own drawer,
 * `process-site`, shown as "06 Process — site notes". One mechanism, no flags.
 *
 * WHY THE ROLE TABLE IS CODE AND NOT POD CONFIG. It is the product's promise,
 * the same for every client. A per-pod override would be a per-client place
 * for a leak to hide, and nobody has asked for one. A pod's staffChat.roles
 * stays the outer gate (who may use the assistant at all); this table narrows
 * what each admitted role reaches.
 *
 * NO DRAWER MEANS OWNER ONLY. A fact or document with no drawer (anything
 * filed before drawers existed, or not yet filed) is reached by the owner and
 * nobody else. Unfiled fails CLOSED; nothing is guessed on the owner's behalf.
 *
 * THE SCHEMA REPEATS THIS LIST in its vocabulary triggers (db/schema.sql),
 * because a trigger cannot import TypeScript. tests/drawers.test.ts fails if
 * the two ever disagree, in either direction.
 *
 * DEPENDENCY-FREE ON PURPOSE, like staff-roles.ts: the worker, the dashboard
 * and the schema tests all import it, and none may drag runtime types in.
 */

/** Every drawer, in the order the owner sees them. Keys are stored; never rename one. */
export const DRAWERS = [
  'company',
  'people',
  'safety',
  'suppliers',
  'clients',
  'process',
  'process-site',
  'templates',
  'public',
] as const

export type Drawer = (typeof DRAWERS)[number]

/** Exact-match membership test. No normalisation: a drawer is an access decision. */
export function isDrawer(value: unknown): value is Drawer {
  return typeof value === 'string' && (DRAWERS as readonly string[]).includes(value)
}

/** What the owner sees. The numbers match the printed card. */
export const DRAWER_LABELS: Readonly<Record<Drawer, string>> = {
  company: '01 Company',
  people: '02 People',
  safety: '03 Safety',
  suppliers: '04 Suppliers',
  clients: '05 Clients',
  process: '06 Process',
  'process-site': '06 Process \u2014 site notes',
  templates: '07 Templates',
  public: 'Website',
}

/**
 * Which drawers each non-owner role reaches — the sign-off table.
 *
 *   office — every drawer. The office runs the paperwork.
 *   site   — 03 Safety, the site notes from 06, 07 Templates, and the
 *            Website drawer (what the firm tells the public is no secret from
 *            its own staff). Never Company, People, Suppliers, Clients or the
 *            office part of Process.
 */
export const STAFF_DRAWERS: Readonly<Record<Exclude<StaffRole, 'owner'>, readonly Drawer[]>> = {
  office: DRAWERS,
  site: ['safety', 'process-site', 'templates', 'public'],
}

/**
 * What one role may reach. The owner's answer is a different KIND, not a
 * longer list: it includes unfiled items, which no list of drawers can name.
 * Callers must handle both kinds, so "the owner sees unfiled" is never lost
 * by someone flattening this into an IN (...) clause.
 */
export type DrawerReach =
  | { kind: 'everything' }
  | { kind: 'drawers'; drawers: readonly Drawer[] }

export function drawerReachFor(role: StaffRole): DrawerReach {
  switch (role) {
    case 'owner':
      return { kind: 'everything' }
    case 'office':
    case 'site':
      return { kind: 'drawers', drawers: STAFF_DRAWERS[role] }
    default: {
      // A role outside the type (a future addition, or a bad cast) reaches
      // NOTHING — never everything. Compile error first, empty list second.
      const unknownRole: never = role
      void unknownRole
      return { kind: 'drawers', drawers: [] }
    }
  }
}

/**
 * Which pods file things in drawers: exactly the pods with a staff chat.
 * Drawers separate what each staff ROLE may be told, so a pod with no staff
 * roles (all of Foredesk) has nothing to separate, and every fact it holds
 * stays unfiled for ever.
 */
export function podUsesDrawers(pod: { staffChat?: unknown }): boolean {
  return pod.staffChat !== undefined
}

/**
 * The drawer a write asked for, checked against the pod it is for.
 *
 *   absent (undefined or null) — unfiled. Always allowed: unfiled is owner
 *     only, so a write that names no drawer can never widen who sees anything.
 *     The owner screens make him choose; the server does not need to, because
 *     the missing choice fails CLOSED. (It also means a dashboard deployed a
 *     few minutes before or after the Worker never breaks an upload.)
 *   a drawer, on a drawer pod — that drawer.
 *   a drawer, on any other pod — REFUSED. Foredesk facts are never filed; a
 *     drawer arriving there is a client bug, and storing it would be a value
 *     that means nothing today and something unintended the day it is read.
 *   anything else — REFUSED. Exact match only (isDrawer).
 */
export type DrawerForWrite =
  | { ok: true; drawer: Drawer | null }
  | { ok: false; reason: 'not_used' | 'unknown' }

export function drawerForWrite(pod: { staffChat?: unknown }, raw: unknown): DrawerForWrite {
  if (raw === undefined || raw === null) return { ok: true, drawer: null }
  if (!podUsesDrawers(pod)) return { ok: false, reason: 'not_used' }
  if (!isDrawer(raw)) return { ok: false, reason: 'unknown' }
  return { ok: true, drawer: raw }
}

/**
 * THE REACH CLAUSE (Kelvo item 2, part 3) — a DrawerReach as SQL, so every
 * read that serves a staff member filters by drawer IN THE QUERY, before
 * anything is ranked, loaded into memory or shown. A row outside the reach
 * never leaves the database, so no later step can mistake it for an answer.
 *
 * Both kinds are handled here, once, so no caller can flatten the owner's
 * "everything" into an IN (...) list and lose the unfiled rows:
 *
 *   everything      -> 1 = 1             unfiled rows included (owner only)
 *   drawers, none   -> 1 = 0             nothing at all
 *   drawers, some   -> col IS NOT NULL AND col IN (?, ...)
 *                                       unfiled rows excluded, by name: IN
 *                                       already refuses NULL, and saying so
 *                                       makes the rule read off the query.
 *
 * FAILS CLOSED on anything the type should have made impossible: an unknown
 * kind, or a drawer outside the vocabulary (dropped, so it can only narrow).
 *
 * `column` is fixed text the caller writes (`f.drawer`, `d.drawer`), never
 * input, and is checked against that shape, so this function can never be
 * the place a query is assembled from something a request supplied.
 */
export interface DrawerReachSql {
  sql: string
  params: Drawer[]
}

const REACH_COLUMN = /^(?:[a-z]+\.)?drawer$/

export function drawerReachSql(column: string, reach: DrawerReach): DrawerReachSql {
  if (!REACH_COLUMN.test(column)) {
    throw new Error(`drawerReachSql: ${JSON.stringify(column)} is not a drawer column`)
  }
  if (reach.kind === 'everything') return { sql: '1 = 1', params: [] }
  if (reach.kind !== 'drawers' || !Array.isArray(reach.drawers)) return { sql: '1 = 0', params: [] }
  const drawers = [...new Set(reach.drawers.filter(isDrawer))]
  if (drawers.length === 0) return { sql: '1 = 0', params: [] }
  return {
    sql: `${column} IS NOT NULL AND ${column} IN (${drawers.map(() => '?').join(', ')})`,
    params: drawers,
  }
}

/**
 * WHO IS ASKING (Kelvo item 2, part 3). Every caller of the answer pipeline
 * says which, and the compiler refuses a caller that does not: the reach of a
 * turn is decided from this, never defaulted.
 *
 *   public — a member of the public: the web chat, WhatsApp, Messenger.
 *   staff  — a signed-in member of the client's staff, with a role from the
 *            verified session.
 */
export type AskedBy = { kind: 'public' } | { kind: 'staff'; role: StaffRole }

/**
 * What one turn may be answered from.
 *
 *   staff  -> drawerReachFor(role). Always, whatever the pod: the role table
 *             is the rule, and a staff turn never borrows the public's reach.
 *   public, on a pod WITHOUT drawers (all of Foredesk) -> everything. Nothing
 *             there is filed, and everything there is the public's to ask
 *             about: the behaviour before drawers, byte for byte.
 *   public, on a pod WITH drawers -> nothing. Such a pod is closed to every
 *             public channel already (publicChatAllowsPod and
 *             staffChatConfigProblems); this is the second lock, so the day
 *             one of those slips, a stranger is answered from no drawer and
 *             no unfiled fact, rather than from all of them.
 *   anything else -> nothing.
 */
export function answerReachFor(pod: { staffChat?: unknown }, askedBy: AskedBy): DrawerReach {
  switch (askedBy?.kind) {
    case 'staff':
      return drawerReachFor(askedBy.role)
    case 'public':
      return podUsesDrawers(pod) ? { kind: 'drawers', drawers: [] } : { kind: 'everything' }
    default:
      return { kind: 'drawers', drawers: [] }
  }
}

/**
 * WHETHER A POD MAY PUT FACTS ON A PUBLIC PAGE (A22, closed 1 Oct 2026).
 *
 * Never on a pod with drawers. Publication is per-fact consent, which is
 * enough where everything the assistant holds is the public's to ask about
 * (Foredesk). On a drawers pod it is not: one click would put a fact from
 * 02 People or 04 Suppliers on a page anyone can read, past every drawer
 * rule above, and nothing in a drawers pod is meant for strangers yet. What
 * the public is told from a client's Website drawer is a later decision, and
 * when it is made it changes THIS function, in one place.
 *
 * Enforced twice: the publish route refuses, and the public page refuses to
 * render a drawers pod at all, so a fact published by any other route — or
 * before this rule — is still never shown.
 */
export function podPublishes(pod: { staffChat?: unknown }): boolean {
  return !podUsesDrawers(pod)
}

